import React from "react";
import {
  PagesContainer,
  PagesWrapper,
  PagesTitle,
  PagesOverviewContainer,
} from "./styles";
import { PageCard, PageMiniCard, PageList } from "@caelor/ui";
import { usePagesStore } from "../../../../../../stores/use-page-store";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import { MACRO_KEY } from "../../../../../defaults";
import { OVERVIEW_DUMMY_DATA } from "./data";
import { buildTemplate, PAGES_TEMPLATES } from "./pages-templates";
import { useStyleguideColors } from "../../../../../../hooks/use-styleguide-colors";

const CardRender = ({ accent, borderRadius }) => (
  <div style={{ display: "flex", flexFlow: "row", gap: 8 }}>
    <div style={{ width: 170 }}>
      <PageCard
        title="Q3 Campaign Brief"
        authorName="Lena Morales"
        description="Workspace for aligning global marketing efforts"
        createdDate="Jun 16, 2025"
        modifiedDate="Jun 16, 2025"
        spaceName="Global Marketing"
        borderRadius={borderRadius}
        likes={24}
        comments={8}
        accentColor={accent}
        isTemplate
      />
    </div>
    <div style={{ width: 170 }}>
      <PageCard
        title="Product Launch"
        authorName="Sofia Varga"
        description="To plan, manage, and track a multi-channel product campaign"
        createdDate="Jun 8, 2025"
        modifiedDate="Jun 8, 2025"
        spaceName="Campaigns"
        borderRadius={borderRadius}
        likes={16}
        comments={8}
        accentColor={accent}
        isTemplate
      />
    </div>
  </div>
);

const MiniCardRender = ({ accent, borderRadius }) => (
  <div style={{ display: "flex", flexFlow: "row", gap: 8 }}>
    <div style={{ width: 167 }}>
      <PageMiniCard
        title="Content Calendar 2025"
        authorName="Marco Elridge"
        createdDate="Apr 16, 2025"
        modifiedDate="Apr 16, 2025"
        spaceName="EMEA Marketing"
        borderRadius={borderRadius}
        accentColor={accent}
        isTemplate
      />
    </div>
    <div style={{ width: 167 }}>
      <PageMiniCard
        title="Q3 Campaign Brief"
        authorName="Lena Morales"
        createdDate="Jun 16, 2025"
        modifiedDate="Jun 16, 2025"
        spaceName="Global Marketing"
        borderRadius={borderRadius}
        accentColor={accent}
        isTemplate
      />
    </div>
  </div>
);

const ListRender = ({ accent, borderRadius }) => (
  <div style={{ display: "flex", flexFlow: "column", gap: 8 }}>
    <div style={{ width: 475 }}>
      <PageList
        title="Post-Launch Report"
        createdDate="Apr 16, 2025"
        modifiedDate="Apr 16, 2025"
        authorName="Sofia Varga"
        spaceName="Campaigns"
        borderRadius={borderRadius}
        likes={24}
        comments={8}
        accentColor={accent}
        isTemplate
      />
    </div>
    <div style={{ width: 475 }}>
      <PageList
        title="Content Calendar 2025"
        createdDate="Apr 16, 2025"
        modifiedDate="Apr 16, 2025"
        authorName="Marco Elridge"
        spaceName="EMEA Marketing"
        borderRadius={borderRadius}
        likes={24}
        comments={8}
        accentColor={accent}
        isTemplate
      />
    </div>
    <div style={{ width: 475 }}>
      <PageList
        title="Q3 Campaign Brief"
        createdDate="Apr 16, 2025"
        modifiedDate="Apr 16, 2025"
        authorName="Lena Morales"
        spaceName="Global Marketing"
        borderRadius={borderRadius}
        likes={24}
        comments={8}
        accentColor={accent}
        isTemplate
      />
    </div>
  </div>
);

const VIEWS = {
  card: { label: "Card", View: CardRender },
  mini_card: { label: "Mini Card", View: MiniCardRender },
  list: { label: "List", View: ListRender },
};

export const OverviewTemplates = () => {
  const { setData } = usePagesStore((state) => ({
    setData: state.setData,
  }));
  const { setMacro, setDrawers, styleguideSetup } = useOpticsStore((state) => ({
    setMacro: state.setMacro,
    setDrawers: state.setDrawers,
    styleguideSetup: state.styleguideSetup,
  }));

  const borderRadius = styleguideSetup?.cornerRadius || 3;

  return (
    <PagesOverviewContainer
      onClick={() => {
        setMacro({ key: MACRO_KEY.PAGES });
        setData({ ...buildTemplate("card", borderRadius) });
        setDrawers({ templates: false, settings: true });
      }}
    >
      {OVERVIEW_DUMMY_DATA.map((page) => (
        <PageCard
          title={page.title}
          authorName={page.authorName}
          description={page.description}
          createdDate={page.modifiedDate}
          modifiedDate={page.modifiedDate}
          coverPictureCallback={() =>
            Promise.resolve(
              "https://caelor-apps.web.app/images/fields/month-week.svg"
            )
          }
          spaceName={page.spaceName}
          borderRadius={borderRadius}
          likes={page.numberOf.likes}
          comments={page.numberOf.comments}
          accentColor={{
            light: "#1D7AFC",
            dark: "#1D7AFC",
          }}
        />
      ))}
    </PagesOverviewContainer>
  );
};

const PagesTemplates = () => {
  const { data, setData, config } = usePagesStore((state) => ({
    data: state.data,
    setData: state.setData,
    config: state.config,
  }));
  const { setDrawers, styleguideSetup } = useOpticsStore((state) => ({
    setDrawers: state.setDrawers,
    styleguideSetup: state.styleguideSetup,
  }));

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });
  const colors = config?.colors;

  const color = colors || styleguideColors;
  const borderRadius =
    config?.cardBorderRadius || styleguideSetup?.cornerRadius || 3;

  const buildData = (template, value) => {
    if (Object.keys(data || {}).length) {
      return {
        ...data,
        config: {
          ...data.config,
          variant: value.variant,
        },
      };
    }

    const newData = {
      ...template,
      config: { ...template.config, ...value },
    };
    return newData;
  };

  return (
    <PagesContainer>
      {PAGES_TEMPLATES.map((template) => {
        const { label, View } = VIEWS[template.config.variant];
        return (
          <PagesWrapper
            key={template.id}
            isMiniCard={template.config.variant === "mini_card"}
            onClick={() => {
              setData({
                ...buildData(template, {
                  colors: color,
                  cardBorderRadius: borderRadius,
                  variant: template.config.variant,
                }),
              });
              setDrawers({ templates: false, settings: true });
            }}
          >
            <PagesTitle>{label}</PagesTitle>
            <View accent={color?.accent} borderRadius={borderRadius} />
          </PagesWrapper>
        );
      })}
    </PagesContainer>
  );
};

export default PagesTemplates;
